import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  useTheme as useMuiTheme,
  Button,
  Avatar,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AutoMode,
  DarkMode,
  Logout as LogoutIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useThemeToggle } from '@/styles';
import { useAuth0 } from '@auth0/auth0-react';
import { useActivateSharedTenant, useSharingConfiguration, useTenant } from '@/hooks/useApi';
import { emitToast } from '@/utils/toast';

interface AppHeaderProps {
  onMenuToggle: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ onMenuToggle }) => {
  const muiTheme = useMuiTheme();
  const { isAuthenticated, user, loginWithRedirect, logout, isLoading } = useAuth0();
  const { isDarkMode, toggleTheme } = useThemeToggle();

  // Theme icon selection based on current mode
  const DarkLightModeButtonIcon = isDarkMode ? AutoMode : DarkMode;

  const userDisplay = user?.name || user?.email || 'User';

  // Tenant switching data (only when authenticated)
  const { data: sharingConfig, refetch: refetchSharing } = useSharingConfiguration(isAuthenticated);
  const { mutateAsync: activateSharedTenant, isPending: isSwitchingTenant } =
    useActivateSharedTenant();

  const { data: tenant, refetch: refetchTenant } = useTenant(isAuthenticated);

  // Avatar menu state
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleSignOut = () => {
    handleCloseMenu();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleSwitchToPrimary = async () => {
    try {
      const activeSt = sharingConfig?.requests?.find(t => t.accepted);
      if (activeSt) {
        await activateSharedTenant({ sharedTenantId: activeSt.id, active: false });
        await refetchSharing();
        await refetchTenant();
        emitToast({ message: 'Switched to primary tenant', severity: 'success' });
      }
    } catch (e: unknown) {
      emitToast({
        message: (e as Error)?.message ?? 'Failed to switch to primary tenant',
        severity: 'error',
      });
    }
    handleCloseMenu();
  };

  const handleActivateShared = async (sharedTenantId: number) => {
    try {
      await activateSharedTenant({ sharedTenantId, active: true });
      await refetchSharing();
      emitToast({ message: 'Switched to shared tenant', severity: 'success' });
    } catch (e: unknown) {
      emitToast({
        message: (e as Error)?.message ?? 'Failed to switch to shared tenant',
        severity: 'error',
      });
    }
    handleCloseMenu();
  };

  return (
    <AppBar
      position='fixed'
      elevation={1}
      sx={{
        zIndex: muiTheme.zIndex.drawer + 1,
        backgroundColor: muiTheme.palette.background.paper,
        color: muiTheme.palette.text.primary,
      }}
    >
      <Toolbar>
        <IconButton
          color='inherit'
          aria-label='open navigation menu'
          onClick={onMenuToggle}
          edge='start'
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
          Budget Speech
        </Typography>

        <IconButton
          color='inherit'
          aria-label={isDarkMode ? 'switch to light mode' : 'switch to dark mode'}
          onClick={toggleTheme}
          sx={{ mr: 0.5 }}
        >
          <DarkLightModeButtonIcon />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isLoading ? (
            <CircularProgress size={20} thickness={5} aria-label='loading' />
          ) : isAuthenticated ? (
            <>
              <Tooltip title={userDisplay}>
                <IconButton
                  color='inherit'
                  aria-label='account menu'
                  aria-controls={menuOpen ? 'account-menu' : undefined}
                  aria-haspopup='true'
                  aria-expanded={menuOpen ? 'true' : undefined}
                  onClick={handleOpenMenu}
                  size='medium'
                >
                  {user?.picture ? (
                    <Avatar alt={userDisplay} src={user.picture} sx={{ width: 28, height: 28 }} />
                  ) : (
                    <Avatar sx={{ width: 28, height: 28 }} aria-label='user avatar'>
                      {userDisplay.slice(0, 1).toUpperCase()}
                    </Avatar>
                  )}
                </IconButton>
              </Tooltip>

              <Menu
                id='account-menu'
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {/* Tenant switching */}
                {tenant?.sharedTenants &&
                  tenant.sharedTenants.some(t => t.accepted) && [
                    <MenuItem key='switch-tenant-header' disabled>
                      <ListItemText primary='Switch tenant' />
                    </MenuItem>,
                    ...tenant.sharedTenants.map(st => (
                      <MenuItem
                        key={st.id}
                        onClick={() => handleActivateShared(st.id)}
                        disabled={isSwitchingTenant}
                      >
                        <ListItemIcon>
                          {st.accepted ? (
                            <CheckIcon fontSize='medium' />
                          ) : (
                            <span style={{ width: 24 }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            sharingConfig?.requests.find(x => x.sharedTenantId === st.id)
                              ?.acceptedTenantName
                          }
                        />
                      </MenuItem>
                    )),
                    ...(tenant.sharedTenants.some(t => t.active)
                      ? [
                          <MenuItem
                            key='use-primary'
                            onClick={handleSwitchToPrimary}
                            disabled={isSwitchingTenant}
                          >
                            <ListItemText primary='Use primary tenant' />
                          </MenuItem>,
                          <Divider key='divider' />,
                        ]
                      : []),
                  ]}

                {/* Sign out */}
                <MenuItem onClick={handleSignOut} aria-label='sign out'>
                  <ListItemIcon>
                    <LogoutIcon fontSize='medium' />
                  </ListItemIcon>
                  <ListItemText primary='Sign out' />
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              color='inherit'
              size='medium'
              aria-label='sign in'
              onClick={() => loginWithRedirect()}
            >
              Sign in
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;
